import type { Metric } from "@/types/metrics";
import type { Capability } from "@/components/OrbusStreamNavigatorComponent/lib/types";

/**
 * Generates target array centered on the API-provided target period year.
 * One real target at 100%, plus 9 back and 9 forward with 10% steps (10%..190%).
 * current year is not used; a valid anchorDate from API is required.
 * @param baseValue The base target value from API
 * @param anchorDate Date string to anchor the real target year (e.g., targetEndDate or targetStartDate)
 * @returns Array of 19 yearly targets from 10% to 190% centered on the anchor year, or undefined if no valid anchor
 */
function generateTargetSegments(
  baseValue: number | null | undefined,
  startDate?: string | null | undefined,
  endDate?: string | null | undefined,
): Array<{ startTs: number; endTs: number; value: number }> | undefined {
  if (baseValue == null) return undefined;
  if (!startDate || !endDate) return undefined;
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return undefined;
  const startSec = Math.floor(start.getTime() / 1000);
  const endSec = Math.floor(end.getTime() / 1000);
  if (endSec <= startSec) return undefined;

  // const segments: Array<{ startTs: number; endTs: number; value: number }> = [];
  // for (let offset = -9; offset <= 9; offset++) {
  //   const percentage = 1 + offset * 0.10; // 0.10 .. 1.90
  //   const segStart = startSec + offset * periodSec;
  //   const segEnd = endSec + offset * periodSec;
  //   segments.push({ startTs: segStart, endTs: segEnd, value: baseValue * percentage });
  // }
  //return segments;
  
  // Use actual target period from API
  return [{ startTs: startSec, endTs: endSec, value: baseValue }];
  
  // Hardcoded 100-year period (2000-2100) - DO NOT USE
  // return [{ startTs: 946684800, endTs: 4102444800, value: baseValue}]
}

/**
 * Converts API response metrics to standardized metric objects
 * @param response The API response containing metrics data
 * @param capabilities Optional list of capabilities to derive Business Capability and ValueStream Stage
 * @returns Array of standardized metric objects
 */
export function convertMetricsFromResponse(response: MetricResponse, capabilities?: Capability[]): Metric[] {
  if (!response || !response.data || !Array.isArray(response.data)) {
    return [];
  }

  return response.data.map((item: MetricData) => {
    // Map Business Capability and ValueStream Stage via Capability.AssessedBy -> keyResult.id
    let businessCapability = "";
    let valueStreamStage = "";
    const metricId = item.keyResult?.id;
    if (capabilities && metricId) {
      const matched = capabilities.find(c => Array.isArray(c.AssessedBy) && c.AssessedBy.includes(metricId));
      if (matched) {
        businessCapability = matched.Name;
        valueStreamStage = matched.Stage;
      }
    }

    return {
      Id: item.keyResult?.id || '',
      "ValueStream Stage": valueStreamStage,
      "Business Capability": businessCapability,
      "KPI Name": item.metric?.label || '',
      "KPI Description": item.description?.Value || '',
      "Input Parameters": '', // Default empty string as not provided by API
      "KPI Formula": item.metricExpression?.Value || '',
      "KPI Objects": '', // Default empty string as not provided by API
      "KPI Attributes": '', // Default empty string as not provided by API
      "Operation": item.metricOperation?.label || '',
      "metricValueKey": item.externalIdentifier?.Value || '',
      "baseline": item.baseline?.Value,
      "target": generateTargetSegments(
        item.target?.Value,
        item.targetStartDate?.Value,
        item.targetEndDate?.Value
      ),
      "targetEndDate": item.targetEndDate?.Value,
      "targetStartDate": item.targetStartDate?.Value,
      "decreasingTarget": item.decreasingTarget?.Value,
      "targetFinancialBenefit": item.targetFinancialBenefit?.Value,
      "metricOperation": item.metricOperation?.label || '',
      "alertingThresholdDuration": item.alertingThresholdDuration?.Value, //duration in days
      "alertingThresholdPercentage": item.alertingThresholdPercentage?.Value,
      "metricCategory": item.metricCategory?.label || '',
      "metricDimension": item.metricDimension?.id || '',
    };
  });
}

// Type definitions (can be moved to types.ts)
export interface MetricResponse {
  model?: any[];
  data: MetricData[];
}

export interface MetricData {
  targetStartDate: any;
  targetEndDate: any;
  alertingThresholdPercentage: any;
  alertingThresholdDuration: any;
  keyResult?: {
    id: string;
    label: string;
    type: string;
  };
  description?: {
    Value: string | null;
  };
  baseline?: {
    Value: number | null;
  };
  target?: {
    Value: number | null;
  };
  decreasingTarget?: {
    Value: boolean | null;
  };
  metricType?: {
    Value: string | null;
  };
  metricDescription?: {
    Value: string | null;
  };
  targetFinancialBenefit?: {
    Value: number | null;
  };
  metric?: {
    id: string;
    label: string;
    type: string;
  };
  externalIdentifier?: {
    Value: string | null;
  };
  metricOperation?: {
    id: string;
    label: string;
    type: string;
  };
  metricExpression?: {
    Value: string | null;
  };
  metricUoM?: {
    Value: string | null;
  };
  targetDate?: {
    Value: string | null;
  };
  metricCategory?: {
    id: string;
    label: string;
    type: string;
  };
  metricDimension?: {
    id: string;
    label: string;
    type: string;
  };
}
